import type { ResourceScope } from '@kop/contracts';
import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';
import type { WorkLedgerPort, WorkLedgerCursor, WorkLedgerProjection, WorkLedgerRow, ExactWorkLedgerSummary } from './work-ledger-port';

/** Read the authorized cursor chain once; display pagination must not limit search or counts. */
export async function loadWorkLedgerCollection(
  port: Pick<WorkLedgerPort,'list'>,scope:ResourceScope,cursor:WorkLedgerCursor|null=null,
  isCurrent:()=>boolean=()=>true,
):Promise<WorkLedgerProjection>{
 const seen=new Set<string>();let result:WorkLedgerProjection|null=null,duplicateRows=false;
 for(let page=0;page<50;page++){
  if(!isCurrent())throw new Error('WORK_CONTROL_STALE_SESSION');
  const key=JSON.stringify(cursor);if(seen.has(key))throw new Error('WORK_CONTROL_LEDGER_CURSOR_REPEATED');seen.add(key);
  const next=await port.list(scope,cursor);
  if(!isCurrent())throw new Error('WORK_CONTROL_STALE_SESSION');
  if(!next.connected||next.errorCode)throw new Error('WORK_CONTROL_LEDGER_READ_FAILED');
  if(!result)result=next;
  else{
   const rows:Map<string,WorkLedgerRow>=new Map(result.rows.map(row=>[row.workId,row]));
   for(const row of next.rows){const previous=rows.get(row.workId);if(previous){duplicateRows=true;if(previous.version>row.version)continue;}rows.set(row.workId,row);}
   const prior:ExactWorkLedgerSummary|null=result.summary,current:ExactWorkLedgerSummary|null=next.summary;
   result={...next,rows:[...rows.values()],heldCount:(result.heldCount??0)+(next.heldCount??0),
    summary:prior&&current?{...current,total:rows.size,needsAction:prior.needsAction+current.needsAction,
     active:prior.active+current.active,patrolOverdue:prior.patrolOverdue+current.patrolOverdue,
     complete:current.complete&&(result.heldCount??0)===0&&!duplicateRows,
     receiptId:`${prior.receiptId}+${current.receiptId}`} : null};
  }
  cursor=next.nextCursor??null;if(!cursor)return result;
 }
 return {...result!,exhausted:false,summary:result?.summary?{...result.summary,complete:false}:null};
}

let generation = 0;
const listeners = new Set<() => void>();
onSensitiveMemoryCacheClear(() => {
  generation += 1;
  for (const listener of listeners) listener();
});

export const workLedgerSessionGeneration = () => generation;
export function subscribeWorkLedgerSession(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function assertWorkLedgerResponseScope(expected: ResourceScope, actual: unknown) {
  if (!actual || typeof actual !== 'object' || Array.isArray(actual))
    throw new Error('WORK_CONTROL_RESPONSE_SCOPE_MISMATCH');
  const value = actual as Record<string, unknown>;
  const siteId = expected.kind === 'tenant' ? null : expected.siteId === 'eumseong' ? 'site-eumseong' : expected.siteId;
  if (expected.kind === 'tenant' || value.kind !== expected.kind || value.tenantId !== expected.tenantId ||
      value.siteId !== siteId || value.contractorId !== (expected.kind === 'contractor' ? expected.contractorId : undefined))
    throw new Error('WORK_CONTROL_RESPONSE_SCOPE_MISMATCH');
}

/** Detail reads may return a contractor record within the server-authorized site. */
export function assertWorkLedgerDetailScope(expected: ResourceScope, actual: ResourceScope) {
  if (expected.kind === 'site' && actual.kind === 'contractor' && actual.contractorId) {
    assertWorkLedgerResponseScope(expected, {
      kind: 'site', tenantId: actual.tenantId, siteId: actual.siteId,
    });
    return;
  }
  assertWorkLedgerResponseScope(expected, actual);
}

/** One mounted actor/site boundary owns this handle; authority clearing invalidates pending results. */
export function createWorkLedgerSession(port: WorkLedgerPort) {
  const capturedGeneration = generation;
  let active = true;
  const assertCurrent = () => {
    if (!active || capturedGeneration !== generation) throw new Error('WORK_CONTROL_STALE_SESSION');
  };
  const guarded = new Proxy(port, {
    get(target, key, receiver) {
      const method: unknown = Reflect.get(target, key, receiver);
      if (typeof method !== 'function') return method;
      return async (...args: unknown[]) => {
        assertCurrent();
        const value: unknown = await Reflect.apply(method, target, args);
        assertCurrent();
        return value;
      };
    },
  });
  return {
    port: guarded,
    activate() { if (capturedGeneration === generation) active = true; },
    dispose() { active = false; },
  };
}
